import React from 'react';
import { DayOfWeek } from '@/models/Schedule';
import { WorkoutType } from '@/models/WorkoutType';
import { useEffectiveBookingsForDate } from '@/hooks/useRecurringBookings';
import { useTimeSlots } from '@/hooks/useTimeSlots';
import EffectiveTimeSlotCard from './EffectiveTimeSlotCard';
import { useTranslation } from 'react-i18next';

interface EffectiveTimeSlotsListProps {
  selectedDate: Date;
  workoutTypes: WorkoutType[];
  selectedWorkoutTypes: Set<string>;
  highlightedTimeSlot?: string | null;
}

const EffectiveTimeSlotsList = ({ selectedDate, workoutTypes, selectedWorkoutTypes, highlightedTimeSlot }: EffectiveTimeSlotsListProps) => {
  const { t } = useTranslation();
  const { timeSlots } = useTimeSlots();
  const { data: effectiveBookings = [], isLoading } = useEffectiveBookingsForDate(selectedDate);

  // Get day of week from selected date using consistent locale
  const dayOfWeek = selectedDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() as DayOfWeek;

  // Filter time slots for the selected day and workout types, then sort by time
  const dayTimeSlots = timeSlots
    .filter(slot => {
      // First filter by day
      if (slot.day !== dayOfWeek) return false;
      
      // Then filter by selected workout types
      // If 'all' is selected, show all workout types
      const isAllSelected = selectedWorkoutTypes.has('all');
      if (isAllSelected) return true;
      return selectedWorkoutTypes.has(slot.workoutTypeId);
    })
    .sort((a, b) => a.startTime.localeCompare(b.startTime));

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="animate-pulse bg-muted rounded-lg h-32" />
        ))}
      </div>
    );
  }

  if (dayTimeSlots.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-lg text-muted-foreground mb-2">
          {t('calendar.noTimeSlotsForDay')}
        </p>
        <p className="text-sm text-muted-foreground">
          {t('calendar.checkScheduleSettings')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {dayTimeSlots.map((timeSlot) => {
        const workoutType = workoutTypes.find(wt => wt.id === timeSlot.workoutTypeId);
        
        // Get effective bookings for this time slot
        // Normalize time formats for comparison (remove seconds if present)
        const normalizeTime = (time: string) => time.substring(0, 5); // Get HH:MM format
        const timeSlotBookings = effectiveBookings.filter(
          booking => normalizeTime(booking.time_slot) === normalizeTime(timeSlot.startTime) && 
                    booking.workout_type_id === timeSlot.workoutTypeId
        );
        
        return (
          <EffectiveTimeSlotCard
            key={timeSlot.id}
            timeSlot={timeSlot}
            workoutType={workoutType}
            selectedDate={selectedDate}
            effectiveBookings={timeSlotBookings}
            isHighlighted={highlightedTimeSlot === timeSlot.startTime}
          />
        );
      })}
    </div>
  );
};

export default EffectiveTimeSlotsList;